'use client';

import React from 'react';
import styled from 'styled-components';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import Breadcrumbs from '@/components/Breadcrumbs';
import HomeElement2 from '@/components/HomeElement2';
import HomeElement3 from '@/components/HomeElement3';
import HomeElement4 from '@/components/HomeElement4';
import HomeElement6 from '@/components/HomeElement6';
import Gallery from '@/components/Gallery';
import FAQSection from '@/components/FAQSection';
import { KeepingGreen, ProofStats, TechStory } from '@/components/FlyerSections';
import { galleryImages } from '@/data/galleryImages';
import { whyGoGreenFaqs } from '@/data/faqData';
import { COLORS } from '@/constants/colors';

/**
 * Why Go Green Page
 * Showcases the benefits of Green Filter products
 *
 * Components:
 * - Hero section with intro and CTA
 * - HomeElement2 (customer reviews)
 * - HomeElement3 (product benefits)
 * - HomeElement4 (benefits text)
 * - HomeElement6 (Green Filter Guarantee - matches home page)
 * - FAQSection
 * - Gallery
 */
export default function WhyGoGreenPage() {
  return (
    <>
      <Header handleClickIndex={() => {}} />
      <div className="header-gap-creator"></div>

      <main>
      <Breadcrumbs items={[{ label: 'Why Green Filter' }, { label: 'Why We Outperform' }]} />
      {/* Hero Section with Intro and CTA */}
      <HeroSection>
        <HeroContent>
          <HeroTitle>Why Choose Green Filter?</HeroTitle>
          <HeroIntro>
            Green Filter high-performance air filters are built for drivers who want better airflow, a washable and
            reusable design, strong filtration, and long-term value. Made in the USA and trusted by performance
            enthusiasts, Green Filter helps your engine breathe better while reducing the need for disposable paper
            filter replacements.
          </HeroIntro>
          <CTAButton href="/shop-by-vehicle">Find Your Green Filter</CTAButton>
        </HeroContent>
      </HeroSection>

      <div className="container-0">
        {/* Customer Reviews Section */}
        <HomeElement2 title="Don't Take Our Word For It" />

        {/* Brochure: how it works and test results */}
        <TechStory />
        <ProofStats detailed />

        {/* Product Benefits Section */}
        <HomeElement3 />

        {/* Benefits Text Section */}
        <HomeElement4 text="The benefits of Green Filter are not just about experiencing immediate gains, but there are long term ones as well." />

        {/* Brochure: environment */}
        <KeepingGreen />

        {/* Green Filter Guarantee Section - Matches Home Page */}
        <HomeElement6 />

        {/* FAQ Section */}
        <FAQSection faqs={whyGoGreenFaqs} title="Frequently Asked Questions" />

        {/* Gallery Section */}
        <Gallery gallery={galleryImages} />
      </div>
      </main>

      <Footer />
    </>
  );
}

// Hero Section Styles
const HeroSection = styled.section`
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  padding: 80px 24px;
  text-align: center;
  border-bottom: 1px solid #e9ecef;

  @media (max-width: 991px) {
    padding: 60px 24px;
  }

  @media (max-width: 767px) {
    padding: 50px 20px;
  }
`;

const HeroContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const HeroTitle = styled.h1`
  font-family: Lato, sans-serif;
  font-size: 48px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 24px;
  line-height: 1.2;

  @media (max-width: 991px) {
    font-size: 40px;
  }

  @media (max-width: 767px) {
    font-size: 32px;
    margin-bottom: 20px;
  }

  @media (max-width: 479px) {
    font-size: 28px;
  }
`;

const HeroIntro = styled.p`
  font-family: Lato, sans-serif;
  font-size: 18px;
  font-weight: 400;
  color: #555;
  margin: 0 0 32px;
  line-height: 1.6;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;

  @media (max-width: 767px) {
    font-size: 16px;
    margin-bottom: 24px;
  }

  @media (max-width: 479px) {
    font-size: 15px;
  }
`;

const CTAButton = styled.a`
  display: inline-block;
  background-color: ${COLORS.brandGreen};
  color: #fff;
  padding: 14px 32px;
  border-radius: 4px;
  font-family: Lato, sans-serif;
  font-size: 16px;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  transition: background-color 0.2s ease, transform 0.2s ease;

  &:hover {
    background-color: #00901c;
    transform: translateY(-2px);
  }

  @media (max-width: 767px) {
    padding: 12px 28px;
    font-size: 15px;
  }
`;
